import { useState, useEffect, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search, Plus, Building2, User } from 'lucide-react'
import { getClienti } from '@/api/clienti'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'

/**
 * Componente autocomplete per ricerca clienti.
 * 
 * Features:
 * - Ricerca con debounce dopo 3 caratteri
 * - Mostra suggerimenti in dropdown
 * - Pulsante "Crea nuovo cliente" se nessun risultato
 * - Supporto keyboard navigation
 * 
 * @param {Object} props
 * @param {Function} props.onSelect - Callback quando cliente selezionato
 * @param {Function} props.onCreateNew - Callback per creare nuovo cliente
 * @param {Object} props.selectedCliente - Cliente attualmente selezionato
 * @param {string} props.placeholder - Placeholder input
 */
export default function ClienteAutocomplete({
  onSelect,
  onCreateNew,
  selectedCliente = null,
  placeholder = "Cerca cliente per nome, email o P.IVA..."
}) {
  const [searchTerm, setSearchTerm] = useState(selectedCliente?.nome_completo || '')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchTerm])

  // Query clienti solo se ci sono almeno 3 caratteri
  const { data: clientiData, isLoading } = useQuery({
    queryKey: ['clienti-search', debouncedSearch],
    queryFn: () => getClienti({ search: debouncedSearch }),
    enabled: (debouncedSearch?.length || 0) >= 3,
  })

  const clienti = clientiData?.data?.results || []
  const searchLen = debouncedSearch?.length || 0
  const showNoResults = searchLen >= 3 && !isLoading && clienti.length === 0
  const showResults = searchLen >= 3 && clienti.length > 0

  // Gestione selezione cliente
  const handleSelect = useCallback((cliente) => {
    onSelect(cliente)
    // Usa nome_completo o costruisci da nome/cognome/ragione_sociale
    const displayName = cliente.nome_completo || cliente.ragione_sociale || `${cliente.nome || ''} ${cliente.cognome || ''}`.trim()
    setSearchTerm(displayName)
    setIsOpen(false)
    setHighlightedIndex(-1)
  }, [onSelect])

  // Gestione creazione nuovo cliente
  const handleCreateNew = useCallback(() => {
    onCreateNew?.()
    setIsOpen(false)
  }, [onCreateNew])

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex(prev => 
          prev < clienti.length - 1 ? prev + 1 : prev
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1)
        break
      case 'Enter':
        e.preventDefault()
        if (highlightedIndex >= 0 && highlightedIndex < clienti.length) {
          handleSelect(clienti[highlightedIndex])
        }
        break
      case 'Escape':
        setIsOpen(false)
        setHighlightedIndex(-1)
        break
    }
  }

  // Mostra cliente selezionato
  useEffect(() => {
    if (selectedCliente) {
      const displayName = selectedCliente.nome_completo || selectedCliente.ragione_sociale || `${selectedCliente.nome || ''} ${selectedCliente.cognome || ''}`.trim()
      setSearchTerm(displayName || '')
    }
  }, [selectedCliente])

  return (
    <div className="relative">
      {/* Input ricerca */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value)
            setIsOpen(true)
            setHighlightedIndex(-1)
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="pl-10"
        />
      </div>

      {/* Dropdown suggerimenti */}
      {isOpen && (searchLen >= 3 || (searchTerm?.length || 0) >= 3) && (
        <Card className="absolute z-50 w-full mt-2 max-h-80 overflow-auto shadow-lg">
          {isLoading && (
            <div className="p-4 text-center text-sm text-muted-foreground">
              Ricerca in corso...
            </div>
          )}

          {showResults && (
            <div className="py-2">
              {clienti.map((cliente, index) => (
                <button
                  key={cliente.id}
                  onClick={() => handleSelect(cliente)}
                  className={cn(
                    "w-full px-4 py-3 text-left hover:bg-accent transition-colors",
                    "flex items-start gap-3 border-b last:border-b-0",
                    highlightedIndex === index && "bg-accent"
                  )}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {/* Icona tipo cliente */}
                  <div className="mt-1">
                    {cliente.tipo === 'azienda' ? (
                      <Building2 className="h-5 w-5 text-primary" />
                    ) : (
                      <User className="h-5 w-5 text-primary" />
                    )}
                  </div>

                  {/* Info cliente */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">
                      {cliente.nome_completo}
                    </p>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span className="truncate">{cliente.email}</span>
                      {cliente.identificativo_fiscale && (
                        <>
                          <span>•</span>
                          <span className="font-mono">
                            {cliente.identificativo_fiscale}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {showNoResults && (
            <div className="p-6 text-center space-y-3">
              <p className="text-sm text-muted-foreground">
                Nessun cliente trovato per "{debouncedSearch}"
              </p>
              {onCreateNew && (
                <Button
                  onClick={handleCreateNew}
                  variant="outline"
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Crea nuovo cliente
                </Button>
              )}
            </div>
          )}

          {searchLen < 3 && (searchTerm?.length || 0) >= 3 && (
            <div className="p-4 text-center text-sm text-muted-foreground">
              Digita almeno 3 caratteri per cercare...
            </div>
          )}
        </Card>
      )}
    </div>
  )
}
